import { type ReactNode } from 'react';

import { t } from 'i18next';
import {
  Controller,
  type FieldPath,
  type FieldValues,
  type PathValue,
} from 'react-hook-form';

import { FormFieldError } from '@/components/Form/FormFieldError';
import { FormFieldHelper } from '@/components/Form/FormFieldHelper';
import { FormFieldItem } from '@/components/Form/FormFieldItem';
import { FormFieldLabel } from '@/components/Form/FormFieldLabel';
import { Select, type SelectProps } from '@/components/Select';

import { type FieldCommonProps } from '../FormField.types';

export type FieldCreatableSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  type: 'creatable-select';
  label?: ReactNode;
  helper?: ReactNode;
  options: Readonly<
    Array<
      Readonly<{
        label: string;
        value: PathValue<TFieldValues, TName>;
      }>
    >
  >;
  onCreateOption?: (inputValue: string) => void;
} & Pick<SelectProps, 'size' | 'placeholder' | 'autoFocus' | 'isLoading'> &
  FieldCommonProps<TFieldValues, TName>;

export const FieldCreatableSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: FieldCreatableSelectProps<TFieldValues, TName>
) => {
  return (
    <Controller
      {...props}
      render={({ field }) => {
        const { value, onChange, ...fieldProps } = field;
        const selectValue =
          props.options?.find((option) => option.value === value) ??
          (value
            ? {
                label: String(value),
                value: value,
              }
            : undefined);

        return (
          <FormFieldItem>
            {!!props.label && <FormFieldLabel>{props.label}</FormFieldLabel>}
            <Select
              type="creatable"
              size={props.size}
              options={props.options}
              placeholder={props.placeholder}
              autoFocus={props.autoFocus}
              value={selectValue}
              onChange={(option) => onChange(option?.value)}
              onCreateOption={(inputValue) => {
                onChange(inputValue);
                props.onCreateOption?.(inputValue);
              }}
              formatCreateLabel={(inputValue) =>
                `${t('common:actions.create')} "${inputValue}"`
              }
              isLoading={props.isLoading}
              {...fieldProps}
            />

            {!!props.helper && (
              <FormFieldHelper>{props.helper}</FormFieldHelper>
            )}
            <FormFieldError />
          </FormFieldItem>
        );
      }}
    />
  );
};
